"use client";

import { setClientToken } from "@onsand/api";
import { auth } from "@onsand/auth";
import { GoogleAuthProvider, OAuthProvider, signInWithPopup } from "firebase/auth";
import { useTranslations } from "next-intl";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { type SocialErrorKey, socialErrorKey } from "@/lib/camp/auth/auth-errors";

const googleProvider = new GoogleAuthProvider();
const appleProvider = new OAuthProvider("apple.com");
appleProvider.addScope("email");
appleProvider.addScope("name");

/** Google and Apple through a popup, for sign-in and sign-up alike: the provider decides which. */
export function SocialButtons({
	onError,
	onSignedIn,
}: {
	/** `null` when the person closed the popup: nothing to say, but the previous error clears. */
	onError: (key: SocialErrorKey | null) => void;
	onSignedIn: () => void;
}) {
	const t = useTranslations("login");
	const [busy, setBusy] = useState(false);

	async function signIn(provider: GoogleAuthProvider | OAuthProvider) {
		setBusy(true);
		onError(null);
		try {
			const credential = await signInWithPopup(auth, provider);
			setClientToken(await credential.user.getIdToken());
			onSignedIn();
		} catch (error) {
			onError(socialErrorKey(error));
			// Only a failure frees the buttons: on success the page is already on its way out.
			setBusy(false);
		}
	}

	return (
		<div className="grid gap-3">
			<Button
				className="h-12 w-full rounded-full text-base sm:h-12 sm:text-base"
				disabled={busy}
				onClick={() => void signIn(googleProvider)}
				type="button"
				variant="outline"
			>
				<svg aria-hidden="true" className="size-5" viewBox="0 0 24 24">
					<path
						d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
						fill="#4285F4"
					/>
					<path
						d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
						fill="#34A853"
					/>
					<path
						d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10.96 10.96 0 001 12c0 1.77.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
						fill="#FBBC05"
					/>
					<path
						d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
						fill="#EA4335"
					/>
				</svg>
				{t("continueWithGoogle")}
			</Button>
			<Button
				className="h-12 w-full rounded-full text-base sm:h-12 sm:text-base"
				disabled={busy}
				onClick={() => void signIn(appleProvider)}
				type="button"
				variant="outline"
			>
				<svg aria-hidden="true" className="size-5" fill="currentColor" viewBox="0 0 24 24">
					<path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
				</svg>
				{t("continueWithApple")}
			</Button>
		</div>
	);
}
